import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  World,
  canPlace,
  utcDateOf,
  type Command,
  type SpeedMultiplier,
  type UiSnapshot,
  SIM_VERSION,
} from '@prism/sim';
import {
  CLASS_COLORS,
  buildFreePlayConfig,
  deriveDailyRun,
  getEnemy,
  getMap,
  BUFF_TILES_BY_ID,
  getTower,
  unlockedContent,
} from '@prism/content';
import { GameLoop } from './loop.js';
import { ShopCard } from './ShopCard.js';
import { SynergyPanel } from './SynergyPanel.js';
import { MetaTree } from './MetaTree.js';
import { TowerInspector } from './TowerInspector.js';
import { RunSummary, type RunSummaryData } from './RunSummary.js';
import { GAME_VERSION, MainMenu, MapSelect } from './MainMenu.js';
import { BossBar } from './BossBar.js';
import { FeedbackForm } from './FeedbackForm.js';
import { sendFeedback } from '../persistence/feedback.js';
import { emptyRunDiscovery, mergeDiscovery } from '../meta/discovery.js';
import { Discovery } from './Discovery.js';
import { SurgeBar } from './SurgeBar.js';
import { isTypingTarget } from './typingTarget.js';
import { IdleSkip, IDLE_SKIP_SPEED } from './idleSkip.js';
import { SELECTABLE_SPEEDS } from './speeds.js';
import { BenchInspector } from './BenchInspector.js';
import { Leaderboard } from './Leaderboard.js';
import { SettingsScreen } from './SettingsScreen.js';
import {
  shouldSubmit,
  buildDailyPayload,
  recordDailyAttempt,
} from '../persistence/daily.js';
import { drainQueue, submitRun, type SubmitOutcome } from '../persistence/submission.js';
import { enemyBrief } from './enemyBrief.js';
import type { Settings } from '../persistence/save.js';
import { loadSave, newPlayerId, writeSave, type SaveData } from '../persistence/save.js';
import { applyRunOutcome, shardsAvailable, type ShardAward } from '../meta/meta.js';
import { MusicPlayer } from '../audio/music.js';
import { WebAudioSink } from '../audio/sfx.js';
import { AudioBus, SilentSink } from '../vfx/audio.js';
import { Renderer } from '../render/renderer.js';
import { Vfx } from '../vfx/vfx.js';
import { DEFAULT_VFX_SETTINGS, type VfxSettings } from '../vfx/vfx.js';

/**
 * Dev harness (TRD-03 verification).
 *
 * NOT the real UI — TRD-09 replaces all of this with the shop cards, synergy panel and
 * tower inspector that actually carry the game's readability. What lives here is the
 * minimum needed to exercise the renderer against a live world: place towers, start waves,
 * watch enemies walk and die.
 */

const hexToInt = (hex: string): number => parseInt(hex.slice(1), 16);

/**
 * The public build shows ONLY the daily run and the leaderboard. Free play, the unlock
 * tree, settings, and the dev-facing run controls (mode toggle, day rotator, Shard
 * readouts) are hidden — not removed — behind this one flag. Shards still accumulate
 * silently in the save, so flipping this back on later loses nothing.
 */
const SHOW_EXTRAS: boolean = false;

/** Real-network deps for `submitRun`/`drainQueue` — tests inject fakes instead. Same
 *  origin (`baseUrl: ''`): /api is proxied to the api service in every deployment. */
const liveSubmitDeps = () => ({
  fetch: window.fetch.bind(window),
  storage: window.localStorage,
  sleep: (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)),
  baseUrl: '',
});

/** Breathing room between waves under auto-start — long enough to hit "auto off". */
const AUTO_START_DELAY_MS = 3000;

/** What the day-rotator surfaces about the run it just built. */
interface DailyInfo {
  seed: bigint;
  mutator: { name: string; description: string } | null;
  generated: boolean;
  probeWave: number;
  attempts: number;
  genMs: number;
}


/** The finale's archetype id — it has its own track and its own glitch treatment. */
const PRISM_ARCHETYPE = 'boss-prism';

/**
 * A fresh 64-bit seed for this run's shop.
 *
 * `crypto.getRandomValues` where it exists, wall-clock as the fallback — the value only
 * has to be unpredictable enough that two players do not collide, and it is recorded in
 * the submission either way, so the run stays exactly replayable.
 */
function freshShopSeed(): bigint {
  const buf = new Uint32Array(2);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(buf);
  else {
    buf[0] = Date.now() >>> 0;
    buf[1] = Math.floor(Math.random() * 0xffffffff) >>> 0;
  }
  return (BigInt(buf[0] ?? 0) << 32n) | BigInt(buf[1] ?? 0);
}

function towerVisual(defId: string) {
  const def = getTower(defId);
  // Schema guarantees exactly two distinct classes (TRD-02 R-02-06), but the inferred
  // type is an array, so `noUncheckedIndexedAccess` still wants the guard.
  const a = def.classes[0];
  const b = def.classes[1];
  return {
    colorA: a ? hexToInt(CLASS_COLORS[a].color) : 0xffffff,
    colorB: b ? hexToInt(CLASS_COLORS[b].accent) : 0xffffff,
    glyph: def.glyph,
  };
}

export function App() {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const loopRef = useRef<GameLoop | null>(null);
  const worldRef = useRef<World | null>(null);
  const rendererRef = useRef<Renderer | null>(null);
  const vfxRef = useRef<Vfx | null>(null);

  const [snapshot, setSnapshot] = useState<UiSnapshot | null>(null);
  const [speed, setSpeed] = useState<SpeedMultiplier>(1);
  /**
   * The player's CHOSEN speed, so the idle skip can borrow the loop's and hand it back.
   * A ref because the sync hook reads it every frame from a closure built once.
   */
  const speedRef = useRef<SpeedMultiplier>(1);
  const idleSkipRef = useRef(new IdleSkip());
  const skippingRef = useRef(false);
  const lastSyncRef = useRef(0);
  /** Mirrored into state only on CHANGE, so the badge can render without a per-frame set. */
  const [skipping, setSkipping] = useState(false);
  const [selectedBench, setSelectedBench] = useState<number | null>(null);
  // Selection is keyed by TILE, not by tower id, because a merge does not upgrade a
  // tower in place — it kills the three copies and spawns a new entity with a new id.
  // An id-keyed selection therefore goes stale on the one action most likely to be
  // followed by another (look at what you just built, then reposition it).
  const [selectedTile, setSelectedTile] = useState<{ x: number; y: number } | null>(null);
  /** The buff tile under the cursor, with the screen point to anchor its tooltip to. */
  const [tileTip, setTileTip] = useState<
    { screenX: number; screenY: number; name: string; description: string; color: string } | null
  >(null);
  const [moveMode, setMoveMode] = useState(false);
  const [fps, setFps] = useState(0);
  const [ready, setReady] = useState(false);
  // The save loads once, before anything reads settings out of it.
  const [metaSave, setMetaSave] = useState<SaveData>(
    () => loadSave(window.localStorage, newPlayerId).save,
  );
  const [vfxSettings, setVfxSettings] = useState<VfxSettings>(() => {
    const s0 = loadSave(window.localStorage, newPlayerId).save.settings;
    return {
      ...DEFAULT_VFX_SETTINGS,
      screenshakeScale: s0.screenshakeScale,
      damageNumbers: s0.damageNumbers,
      showDamageFlash: s0.showDamageFlash,
      hitstopEnabled: s0.hitstopEnabled,
    };
  });

  // Day rotation (dev only). The harness used to pin one authored map and one fixed seed,
  // which meant the generator — the thing that decides every daily — was never once
  // exercised by actually playing it. Offset in days from today's UTC date.
  const [dayOffset, setDayOffset] = useState(0);
  const [daily, setDaily] = useState<DailyInfo | null>(null);
  const date = useMemo(() => utcDateOf(Date.now() + dayOffset * 86_400_000), [dayOffset]);
  /** For callbacks that outlive a render — the award must key the run's own date. */
  const dateRef = useRef(date);
  dateRef.current = date;
  const modeRef = useRef<'daily' | 'free'>('daily');
  const freeMapRef = useRef('the-furrow');

  // Rebuilding the world builds a fresh Vfx too, so the toggles' state has to be carried
  // across rather than silently reverting to defaults.
  const vfxSettingsRef = useRef(vfxSettings);

  const updateVfx = useCallback((patch: Partial<VfxSettings>) => {
    setVfxSettings((prev) => {
      const next = { ...prev, ...patch };
      vfxSettingsRef.current = next;
      vfxRef.current?.applySettings(next);
      return next;
    });
  }, []);

  const dispatch = useCallback((cmd: Command) => {
    loopRef.current?.dispatch(cmd);
  }, []);

  /**
   * Theme music. Browsers refuse audio before a user gesture, so the player arms on
   * the FIRST pointerdown/keydown anywhere — menu click, daily click, whatever comes
   * first — and runs across every screen from then on. Volume follows the settings
   * (master × music) live, so the sliders are audible while dragged.
   */
  const musicRef = useRef<MusicPlayer | null>(null);
  if (musicRef.current === null) musicRef.current = new MusicPlayer();
  /**
   * ONE bus for the session, handed to every Vfx.
   *
   * A Vfx is rebuilt on every world, and a per-Vfx bus meant the decoded samples and the
   * player's volume both died with the run that loaded them. The bus outliving the world
   * is also what lets the sink boot on a gesture at the menu and still be playing towers
   * three runs later.
   */
  const audioBusRef = useRef<AudioBus | null>(null);
  if (audioBusRef.current === null) audioBusRef.current = new AudioBus(new SilentSink());
  const sfxBootedRef = useRef(false);

  const bootSfx = useCallback(async () => {
    if (sfxBootedRef.current) return;
    sfxBootedRef.current = true;
    const bus = audioBusRef.current;
    if (!bus) return;
    const ctx = new AudioContext();
    if (ctx.state === 'suspended') await ctx.resume();
    const sink = new WebAudioSink(ctx, ctx.destination, (handle) => bus.release(handle));
    await sink.load();
    // Nothing decoded — every file missing or undecodable. Staying on the silent sink
    // costs nothing and keeps the voice limiter honest about what is actually audible.
    if (sink.loadedCount > 0) bus.setSink(sink);
  }, []);
  useEffect(() => {
    const music = musicRef.current;
    if (!music) return;
    const onFirstGesture = () => {
      music.start();
      // Samples boot on the same gesture and for the same reason: an AudioContext
      // created without one is born suspended, and the first shot of the run would be
      // silent with no way to tell that from a missing file.
      //
      // Its own context, not the music player's. They are separate mixes on separate
      // sliders, the music context is private to the loop math in there, and the cost
      // of a second context is one more entry against the browser's handful.
      void bootSfx().catch(() => {
        // Silence is a supported state — see WebAudioSink.
      });
      window.removeEventListener('pointerdown', onFirstGesture);
      window.removeEventListener('keydown', onFirstGesture);
    };
    window.addEventListener('pointerdown', onFirstGesture);
    window.addEventListener('keydown', onFirstGesture);
    return () => {
      window.removeEventListener('pointerdown', onFirstGesture);
      window.removeEventListener('keydown', onFirstGesture);
    };
  }, []);
  useEffect(() => {
    musicRef.current?.setVolume(metaSave.settings.masterVolume * metaSave.settings.musicVolume);
  }, [metaSave.settings.masterVolume, metaSave.settings.musicVolume]);

  /**
   * Hover/selection range circle (playtest UX request): the hovered tower wins, the
   * selected tower is the fallback. Resolved against the LATEST snapshot on every
   * call, so a merge or synergy change moves the circle instead of leaving it stale.
   * Refs are declared here so the pointer handlers below can close over them; the
   * `.current` assignments live where `snapshot`/`selected` are derived.
   */
  const snapshotRef = useRef<UiSnapshot | null>(null);
  const selectedTowerRef = useRef<UiSnapshot['placedTowers'][number] | undefined>(undefined);
  const hoverTileRef = useRef<{ x: number; y: number } | null>(null);
  const syncFocus = useCallback(() => {
    const renderer = rendererRef.current;
    if (!renderer) return;
    const snap = snapshotRef.current;
    const hover = hoverTileRef.current;
    const hovered =
      hover && snap
        ? snap.placedTowers.find((t) => t.tileX === hover.x && t.tileY === hover.y)
        : undefined;
    const focus = hovered ?? selectedTowerRef.current;
    renderer.setFocusRange(
      focus ? { tileX: focus.tileX, tileY: focus.tileY, range: focus.range } : null,
    );
  }, []);

  /**
   * Auto-start: when a wave clears, count down briefly and dispatch `startWave`.
   *
   * CLIENT automation, deliberately not a sim rule: it fires the same command a click
   * would, through the same dispatch, so the replay log records a normal `startWave` at
   * a tick and the server replays the run identically (R-00-07). A sim-side auto-start
   * would be a SIM_VERSION bump for what is really a convenience preference.
   *
   * The countdown is the off-ramp — "turn it off when it gets tough" needs a moment
   * between waves where the toggle still matters. Wave 0's build phase never arms:
   * an empty board auto-starting wave 1 is a lost run on a timer.
   */
  const [autoStart, setAutoStart] = useState(false);
  const [autoCountdown, setAutoCountdown] = useState<number | null>(null);

  /**
   * Pause (Esc): stops the frame loop outright — the sim, VFX and render sync all halt
   * on the current frame, which is exactly what a pause should look like. Replay-safe
   * by construction: no ticks run while paused, so nothing about the run's outcome can
   * depend on how long the player thought.
   */
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  useEffect(() => {
    pausedRef.current = paused;
    const loop = loopRef.current;
    if (!loop) return;
    if (paused) loop.stop();
    else if (!loop.running) loop.start();
  }, [paused]);

  /**
   * Paused music plays through the wall (low-pass + a small room), rather than ducking.
   *
   * The AudioContext keeps running while the frame loop is stopped, which is what makes
   * this audible at all — and what makes the volume sliders in the pause panel tune
   * against the real track.
   */
  useEffect(() => {
    musicRef.current?.setMuffled(paused);
  }, [paused]);

  /**
   * The shop is a right-side PANEL now (per the Figma layout), not a permanent bar:
   * it slides in for every build phase and gets out of the way when the wave starts.
   * The canvas reserves the space rather than being covered — the battlefield never
   * hides gameplay under UI.
   */
  const [shopOpen, setShopOpen] = useState(true);

  // ── Meta progression (TRD-08 Part B) ────────────────────────────────────────────────
  /** Read at world-build time WITHOUT being an effect dependency — a Shard award mid-run
   *  must not tear the run down. Tree purchases apply from the next run. */
  const metaSaveRef = useRef<SaveData | null>(null);
  const [treeOpen, setTreeOpen] = useState(false);
  const [lastAward, setLastAward] = useState<ShardAward | null>(null);

  /**
   * Free play: the mode where the tree MATTERS. A free run's config filters to the
   * player's unlocks and applies their effect nodes (R-08-19a); dailies never do
   * (R-08-22 — the server derives the daily config purely from the seed and could not
   * reproduce a run shaped by a tree it cannot see).
   *
   * `runVersion` forces a world rebuild on demand — free-play seeds are wall-clock
   * based, so each bump is a fresh run.
   */
  const [summary, setSummary] = useState<RunSummaryData | null>(null);
  /**
   * Whether this summary owes a feedback prompt, decided ONCE when the run ends.
   *
   * Not re-derived from `lastFeedbackDate` on every render, which is the obvious version
   * and is wrong: submitting stamps that date, the condition flips mid-render, and the
   * form unmounts on the very click that submits it — so the player gets no thank-you and
   * no sign anything was sent. The answer to "were you owed a prompt" is a fact about the
   * moment the run ended, not about the save's current contents.
   */
  const [feedbackDue, setFeedbackDue] = useState(false);
  /**
   * What this run has met, accumulated as it plays and folded into the permanent save
   * when it ends (playtest request).
   *
   * A ref, not state: it is written on the event path several times a second and nothing
   * renders from it until the run is over, so making it state would re-render the whole
   * game to update a compendium nobody is looking at.
   */
  const runDiscoveryRef = useRef(emptyRunDiscovery());
  /** What became of this run's submission — shown on the summary, see RunSummary. */
  const [submitState, setSubmitState] = useState<SubmitOutcome | 'sending' | null>(null);
  /**
   * The summary is DISMISSED, not discarded, when the player goes to look at the board.
   *
   * "View final board" used to clear it outright, which made looking at your own board a
   * one-way door: the score, the wave count and the damage breakdown were gone for good
   * and the only way back was to play another run. Keeping the data and hiding the panel
   * costs nothing and makes the button a toggle.
   */
  const [summaryHidden, setSummaryHidden] = useState(false);

  /** TRD-09 §4: MainMenu → (MapSelect →) Run. The tree is an overlay over any screen. */
  const [screen, setScreen] = useState<
    'menu' | 'maps' | 'run' | 'board' | 'settings' | 'discovery'
  >('menu');

  /** The menu has its own song. Every screen that is not a run is "the menu". */
  useEffect(() => {
    musicRef.current?.setMenu(screen !== 'run');
  }, [screen]);
  /** First-ever run: ask for a leaderboard name before it counts (backlog B-13). */
  const [namePrompt, setNamePrompt] = useState(false);
  const [mode, setMode] = useState<'daily' | 'free'>('daily');
  const [freeMap, setFreeMap] = useState('the-furrow');
  const [runVersion, setRunVersion] = useState(0);
  const unlockedMaps = unlockedContent(metaSave.ownedNodes).maps;

  const updateSave = useCallback((next: SaveData) => {
    setMetaSave(next);
    metaSaveRef.current = next;
    writeSave(window.localStorage, next);
  }, []);

  // The ref shadows the state so run-end callbacks (which outlive renders) always read
  // the latest save, however it was updated — updateSave, settleRun, or the settings
  // screen. It is NOT an effect dependency anywhere, so syncing it never tears a run down.
  useEffect(() => {
    metaSaveRef.current = metaSave;
  }, [metaSave]);

  /**
   * Settings changes persist to the save IMMEDIATELY (R-09-28), and the VFX-facing
   * subset is mirrored into the live Vfx so a shake slider is felt on the next hit,
   * not after a reload. The in-run Feel buttons route through here too — one path,
   * one source of truth, no "settings say on but the run says off" drift.
   */
  const changeSettings = useCallback(
    (patch: Partial<Settings>) => {
      setMetaSave((prev) => {
        const next = { ...prev, settings: { ...prev.settings, ...patch } };
        writeSave(window.localStorage, next);
        return next;
      });
      const vfxPatch: Partial<VfxSettings> = {};
      if (patch.screenshakeScale !== undefined) vfxPatch.screenshakeScale = patch.screenshakeScale;
      if (patch.damageNumbers !== undefined) vfxPatch.damageNumbers = patch.damageNumbers;
      if (patch.showDamageFlash !== undefined) vfxPatch.showDamageFlash = patch.showDamageFlash;
      if (patch.hitstopEnabled !== undefined) vfxPatch.hitstopEnabled = patch.hitstopEnabled;
      if (Object.keys(vfxPatch).length > 0) updateVfx(vfxPatch);
      // Master/SFX mirror into the live AudioBus the same way — the future SFX sink
      // reads these on every play(). Music reaches the MusicPlayer via its own effect.
      const bus = vfxRef.current?.audio;
      if (bus) {
        if (patch.masterVolume !== undefined) bus.settings.master = patch.masterVolume;
        if (patch.sfxVolume !== undefined) bus.settings.sfx = patch.sfxVolume;
      }
    },
    [updateVfx],
  );

  const changeDisplayName = useCallback((name: string) => {
    setMetaSave((prev) => {
      const next = { ...prev, displayName: name };
      writeSave(window.localStorage, next);
      return next;
    });
  }, []);

  /**
   * Submit the daily the moment it ends (TRD-10 §5). Fired from the same event that
   * banks Shards, so a tabbed-away 4× run still submits. Sent whenever the run beats
   * what has already been submitted today — the server keeps the better of the two, so
   * anything that could not improve the row is not worth a replay slot.
   * An accepted or queued outcome records the attempt as submitted; a terminal rejection
   * records it as unsubmitted (the server never counted it), which leaves the next run
   * free to try again.
   */
  const submitDaily = useCallback((world: World, wavesCleared: number) => {
    if (modeRef.current !== 'daily') return;
    const save = metaSaveRef.current;
    if (!save) return;
    const date = dateRef.current;
    const payload = buildDailyPayload(world, loopRef.current?.commandLog ?? [], save, date);
    if (!shouldSubmit(save, date, payload.claimedScore)) return;
    setSubmitState('sending');
    void submitRun(payload, liveSubmitDeps()).then((outcome) => {
      setSubmitState(outcome);
      const submitted = outcome.status !== 'rejected';
      setMetaSave((prev) => {
        const next = recordDailyAttempt(
          prev,
          { date, score: payload.claimedScore, wavesCleared },
          submitted,
        );
        writeSave(window.localStorage, next);
        return next;
      });
    });
  }, []);

  // Runs queued on a flaky connection drain on next boot (R-10-19). Once, fire-and-forget.
  useEffect(() => {
    void drainQueue(liveSubmitDeps());
  }, []);

  /**
   * Award Shards when the run ends (R-08-16). Fired from the event stream, so an
   * auto-started, 4×-speed, tabbed-away run still pays out — anything that ends a run
   * emits `runEnded`, and this is the only listener that makes it permanent.
   *
   * Keyed by the daily date: dailies have no stable map id (the map is generated), and
   * the date IS the run's identity. `bestWave`/`winsPerMap` under a date key behave
   * exactly like a map key — each daily is its own "map" you can first-win once.
   */
  const settleRun = useCallback((wavesCleared: number, victory: boolean) => {
    setMetaSave((prev) => {
      const { save: next, award } = applyRunOutcome(prev, {
        wavesCleared,
        victory,
        // A daily's identity is its date; a free-play run's is its map.
        mapKey: modeRef.current === 'free' ? freeMapRef.current : `daily-${dateRef.current}`,
      });
      // Fold the run's Discovery in here rather than in its own setState: two writers
      // racing on the same save is how one of them silently loses.
      next.discovery = mergeDiscovery(prev.discovery, runDiscoveryRef.current);
      writeSave(window.localStorage, next);
      setLastAward(award);
      return next;
    });
  }, []);
  const phase = snapshot?.phase;
  const wave = snapshot?.wave ?? 0;

  // Auto-manage the shop panel around the phase: open for every build, away in combat.
  useEffect(() => {
    if (phase === 'build') setShopOpen(true);
    else if (phase === 'combat') setShopOpen(false);
  }, [phase]);

  useEffect(() => {
    if (!autoStart || phase !== 'build' || wave < 1 || paused) {
      setAutoCountdown(null);
      return;
    }

    const deadline = Date.now() + AUTO_START_DELAY_MS;
    setAutoCountdown(AUTO_START_DELAY_MS);

    const timer = setInterval(() => {
      const left = deadline - Date.now();
      if (left > 0) {
        setAutoCountdown(left);
        return;
      }
      clearInterval(timer);
      setAutoCountdown(null);
      dispatch({ t: 'startWave' });
    }, 100);

    // Any exit re-runs this effect with fresh conditions: toggled off, wave started
    // (manually or by us), or the run torn down by the day rotator.
    return () => clearInterval(timer);
  }, [autoStart, phase, wave, paused, dispatch]);

  /**
   * Synergy-row hover → range circles on every contributing tower (R-09-17, R-03-24).
   *
   * Resolved from the CURRENT snapshot via a ref, not from a captured one: the hover can
   * outlive several publishes, and a merge mid-hover would otherwise leave a circle on a
   * tower that no longer exists.
   */
  const hoveredClassRef = useRef<string | null>(null);
  const onHoverClass = useCallback((classId: string | null) => {
    hoveredClassRef.current = classId;
    const snap = worldRef.current?.snapshotForUI();
    const renderer = rendererRef.current;
    if (!renderer) return;
    if (classId === null || !snap) {
      renderer.setClassHighlight(null);
      return;
    }
    const members = new Set(
      snap.synergies.find((sy) => sy.classId === classId)?.contributingTowers ?? [],
    );
    renderer.setClassHighlight(snap.placedTowers.filter((t) => members.has(t.id)));
  }, []);

  // Keyed on `date`: rotating the day tears the whole run down and rebuilds it, reusing
  // the teardown that already had to be correct for unmount. Off the run screen there is
  // no world at all — the menu costs nothing while it sits there.
  useEffect(() => {
    if (screen !== 'run') return;
    let disposed = false;
    let renderer: Renderer | null = null;
    let loop: GameLoop | null = null;

    // Selections point into a world that is about to stop existing.
    setReady(false);
    setSnapshot(null);
    setSummary(null);
    setSubmitState(null);
    setSummaryHidden(false);
    setSelectedBench(null);
    setSelectedTile(null);
    setMoveMode(false);
    setPaused(false);
    setShopOpen(true);

    void (async () => {
      const host = hostRef.current;
      if (!host) return;

      modeRef.current = mode;
      freeMapRef.current = freeMap;

      let world: World;
      let mapForRenderer;
      // A fresh run means a fresh observation log — this effect rebuilds the world, and
      // carrying the previous run's set across would double-count its kills on settle.
      runDiscoveryRef.current = emptyRunDiscovery();

      let mutatorName: string | null = null;
      if (mode === 'free') {
        // Free play: the run the TREE shapes. Seeded from the wall clock — free runs
        // are not submitted anywhere, so non-reproducible seeds are fine (the sim
        // itself stays deterministic per seed). Unlocks are read through a ref so a
        // Shard award mid-run does not rebuild the world under the player.
        const owned = (metaSaveRef.current ?? metaSave).ownedNodes;
        world = new World(
          BigInt(Date.now()),
          buildFreePlayConfig(freeMap, unlockedContent(owned)),
          { shopSeed: freshShopSeed() },
        );
        mapForRenderer = getMap(freeMap);
        setDaily(null);
      } else {
        // The daily IS the run: map, waves, mutator and seed all come out of the date.
        // This is the same call the server makes, so what renders here is what gets
        // replayed.
        const t0 = performance.now();
        const run = deriveDailyRun(date);
        const genMs = Math.round(performance.now() - t0);
        // The map, the waves and the mutator are the DAY — shared by everyone. The
        // shop is not: it rolls fresh every run, so two players on the same daily draft
        // different towers and a restart is a genuine re-roll (playtest request).
        world = new World(run.seed, run.config, { shopSeed: freshShopSeed() });
        mapForRenderer = run.map;
        mutatorName = run.mutator?.name ?? null;
        setDaily({
          seed: run.seed,
          mutator: run.mutator
            ? { name: run.mutator.name, description: run.mutator.description }
            : null,
          generated: run.generated,
          probeWave: run.probeWave,
          attempts: run.attempts,
          genMs,
        });
      }
      worldRef.current = world;

      renderer = await Renderer.create(host, towerVisual);
      if (disposed) {
        renderer.destroy();
        return;
      }
      rendererRef.current = renderer;

      // Buff tiles resolved to presentation here, so the renderer never imports content.
      renderer.setMap({
        ...mapForRenderer,
        buffTiles: world.config.buffTiles.map((t) => {
          const def = BUFF_TILES_BY_ID[t.kind];
          return {
            x: t.x,
            y: t.y,
            color: def ? Number.parseInt(def.color.slice(1), 16) : 0xffffff,
            particle: def?.particle ?? 'zoneMote',
          };
        }),
      });

      const vfx = new Vfx(audioBusRef.current ?? undefined);
      vfx.applySettings(vfxSettingsRef.current);
      // The shared bus survives world rebuilds now, but seed it anyway: on the very
      // first world it is still on DEFAULT_AUDIO_SETTINGS and the player's saved volume
      // has to win before the first shot, not after the first slider drag.
      const savedAudio = metaSaveRef.current?.settings;
      if (savedAudio) {
        vfx.audio.settings.master = savedAudio.masterVolume;
        vfx.audio.settings.sfx = savedAudio.sfxVolume;
      }
      vfx.tintFor = (defId) => towerVisual(defId).colorA;
      // Run-level effects (wave start, wave clear, run end) stage at the Core, so the VFX
      // layer is told where that is rather than reaching into the world for it.
      vfx.setCore(world.config.core.x + 0.5, world.config.core.y + 0.5);
      renderer.attachVfx(vfx);
      vfxRef.current = vfx;

      loop = new GameLoop(world, {
        onSync: (alpha) => {
          renderer?.sync(world, alpha);

          // Fast-forward the stretches with nothing to shoot (playtest report) — see
          // `idleSkip.ts`. Speed scales wall time into a fixed-step accumulator, so this
          // shortens the WAIT and never the run: the tick sequence, and the hash the
          // server checks it against, are untouched.
          const now = performance.now();
          const dt = lastSyncRef.current === 0 ? 16 : now - lastSyncRef.current;
          lastSyncRef.current = now;
          if ((metaSaveRef.current ?? metaSave).settings.autoFastForward) {
            const engaged = idleSkipRef.current.update(world.phase, dt);
            const want = engaged ? IDLE_SKIP_SPEED : speedRef.current;
            if (loop && loop.speed !== want) loop.speed = want;
            if (engaged !== skippingRef.current) {
              skippingRef.current = engaged;
              setSkipping(engaged);
            }
          }
          // "Met" comes from the BOARD, not from the event stream: `enemyDamaged` does
          // not carry an archetype, and an enemy that walks past untouched emits nothing
          // identifying at all — which is exactly the one you most want to look up.
          const log = runDiscoveryRef.current;
          const items = world.enemies.items;
          for (let i = 0; i < items.length; i++) {
            const e = items[i];
            if (e && e.alive) log.enemiesSeen.add(e.archetype);
          }
        },
        onEvents: (events) => {
          renderer?.handleEvents(events, world);
          // Anything taking damage means the wave is still happening — restart the quiet
          // clock before anything else looks at it this frame.
          idleSkipRef.current.noteEvents(events);
          // VFX reads the event stream and nothing else — never world state (R-04-01).
          vfx.handleEvents(events, loop?.speed ?? 1);
          const freeze = vfx.takeHitstop();
          if (freeze > 0) loop?.freeze(freeze);
          for (const ev of events) {
            // Discovery: what got fielded, what got met, what got killed. Taken from the
            // event stream rather than from the final board, so a tower sold on wave 4
            // and an enemy that leaked both still count as discovered.
            const log = runDiscoveryRef.current;
            if (ev.t === 'towerPlaced') log.towers.add(ev.defId);
            if (ev.t === 'enemyKilled') {
              log.enemiesSeen.add(ev.archetype);
              log.kills.set(ev.archetype, (log.kills.get(ev.archetype) ?? 0) + 1);
            }
            if (ev.t !== 'runEnded') continue;
            settleRun(ev.wavesCleared, ev.victory);
            submitDaily(world, ev.wavesCleared);
            // Snapshot the ended world NOW — phase is 'ended', nothing mutates further,
            // and the summary must show the board as it died, not as React catches up.
            const finalSnap = world.snapshotForUI();
            setFeedbackDue((metaSaveRef.current ?? metaSave).lastFeedbackDate !== date);
            setSummary({
              victory: ev.victory,
              wavesCleared: ev.wavesCleared,
              totalWaves: world.config.totalWaves,
              score: ev.score,
              goldEarned: world.economy.goldEarnedTotal,
              coreHp: world.coreHp,
              coreMaxHp: world.coreMaxHp + world.runMods.bonusCoreHp,
              towers: finalSnap.placedTowers,
              synergies: finalSnap.synergies,
              mode: modeRef.current,
              mutator: mutatorName,
            });
          }
        },
        onVfx: (deltaMs) => {
          vfx.update(deltaMs);
          renderer?.update(deltaMs, world);
          renderer?.drawDebug(world);
        },
        onPublish: () => {
          const snap = world.snapshotForUI();
          setSnapshot(snap);
          setFps(loop?.fps ?? 0);
          // Prism gets its own song. Checked on the ~20 Hz publish rather than per tick,
          // and `setBoss` is idempotent, so this is a scan and two no-ops most frames.
          let prismAlive = false;
          for (const e of world.enemies.items) {
            if (e.alive && e.archetype === PRISM_ARCHETYPE) {
              prismAlive = true;
              break;
            }
          }
          musicRef.current?.setBoss(prismAlive);
          // B-5: pulse a ★ over every placed tower whose merge is ready.
          renderer?.setMergeHints(
            snap.placedTowers.filter((t) => t.mergeReady).map((t) => t.id),
          );
          // Re-resolve a live class-hover against the fresh state, so a merge or sell
          // mid-hover moves the circles instead of leaving one on a dead tower.
          if (hoveredClassRef.current !== null) onHoverClass(hoveredClassRef.current);
          syncFocus();
        },
      });
      loopRef.current = loop;
      loop.start();
      setReady(true);

      // Dev-only inspection handle. The debug overlay (TRD-03 §11) covers what you can
      // see; this covers what you can't — entity counts, economy state, the run hash.
      if (import.meta.env.DEV) {
        (window as unknown as { __prism: unknown }).__prism = { world, loop, renderer, vfx, music: musicRef.current };
      }
    })();

    const onResize = () => rendererRef.current?.resize();
    // Shop shortcuts (R-09-10): 1–4 buy, R reroll, Space start wave, Esc deselect.
    // Commands go through `dispatch` like every click does, so a keyboard buy is in the
    // replay log identically to a mouse buy (R-09-04). Validity is not checked here —
    // the sim rejects an unaffordable buy as a logged no-op (TRD-07 R-07-07), which is
    // both simpler and more honest than duplicating the affordability rule in a closure
    // that would go stale against a 20 Hz snapshot.
    const onKey = (e: KeyboardEvent) => {
      // Nothing at all while the player is typing. This listener is on `window`, so
      // without it every hotkey fires from inside a text field — see `isTypingTarget`.
      if (isTypingTarget(e.target)) return;

      // Esc is the only key that works while paused — everything else would just queue
      // commands into a stopped loop.
      if (e.key === 'Escape') {
        if (pausedRef.current) {
          setPaused(false);
        } else if (hasSelectionRef.current) {
          setSelectedBench(null);
          setSelectedTile(null);
          setMoveMode(false);
        } else {
          setPaused(true);
        }
        return;
      }
      if (pausedRef.current) return;
      if (e.key === '`' && rendererRef.current) {
        rendererRef.current.debugVisible = !rendererRef.current.debugVisible;
        return;
      }
      if (e.key >= '1' && e.key <= '9') {
        // The number keys mean different things in the two phases, which costs nothing
        // because the two actions cannot both be legal: buying is build-only and the
        // surge is combat-only. Reusing 1–9 keeps the one mid-wave input on the keys a
        // player's hand is already on, instead of inventing a second row to learn.
        const index = Number(e.key) - 1;
        if (snapshotRef.current?.phase === 'combat') {
          // Insertion order, straight off the snapshot — the same order the badges on the
          // board are numbered in, so "press 2" and "the tower marked 2" cannot disagree.
          const support = snapshotRef.current.placedTowers.filter((t) => t.support);
          const target = support[index];
          if (target) dispatch({ t: 'surge', tower: target.id });
        } else if (index < 4) {
          dispatch({ t: 'buy', slot: index });
        }
      } else if (e.key === 'r' || e.key === 'R') {
        dispatch({ t: 'reroll' });
      } else if (e.key === ' ') {
        // Space also scrolls the page and re-clicks a focused button — both wrong here.
        e.preventDefault();
        dispatch({ t: 'startWave' });
      }
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('keydown', onKey);

    return () => {
      disposed = true;
      window.removeEventListener('resize', onResize);
      window.removeEventListener('keydown', onKey);
      loop?.stop();
      renderer?.destroy();
      loopRef.current = null;
      rendererRef.current = null;
      worldRef.current = null;
      vfxRef.current = null;
    };
    // metaSave is deliberately absent: unlocks are snapshotted per run via the ref, so
    // a Shard award mid-run cannot tear the world down under the player.
  }, [screen, date, mode, freeMap, runVersion, dispatch, onHoverClass, syncFocus]);

  // Placement ghost follows the cursor while a bench item is selected.
  /**
   * The range a tower would ACTUALLY have on this tile.
   *
   * The ghost drew the printed range, so hovering a Vantage Point promised one circle and
   * placing gave another (playtest request). The bonus is read off the run config rather
   * than recomputed here — the sim owns what a tile does, and a second implementation in
   * the UI is exactly how the two drift apart.
   */
  const rangeOnTile = useCallback((baseRange: number, tileX: number, tileY: number): number => {
    const tile = worldRef.current?.config.buffTiles.find((t) => t.x === tileX && t.y === tileY);
    return baseRange + (tile?.rangeBonus ?? 0);
  }, []);

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const renderer = rendererRef.current;
      const world = worldRef.current;
      if (!renderer || !world) return;

      if (selectedBench === null) {
        // Move mode gets the same ghost placement does: the actual tower, its
        // EFFECTIVE range, and the validity tint (backlog B-6).
        const moving = moveMode ? selectedTowerRef.current : undefined;
        if (moving) {
          hoverTileRef.current = null;
          renderer.setFocusRange(null);
          const moveTile = renderer.screenToTile(e.clientX, e.clientY);
          if (moveTile) {
            const ok = canPlace(world.config.buildMask, world.towers, moveTile.tileX, moveTile.tileY);
            // `moving.range` is the tower's EFFECTIVE range, which already includes the
            // tile it is standing on — so strip that before adding the destination's.
            const from = worldRef.current?.config.buffTiles.find(
              (t) => t.x === moving.tileX && t.y === moving.tileY,
            );
            const printed = moving.range - (from?.rangeBonus ?? 0);
            renderer.setGhost(
              moving.defId,
              moveTile.tileX,
              moveTile.tileY,
              ok,
              rangeOnTile(printed, moveTile.tileX, moveTile.tileY),
            );
            return;
          }
        }
        renderer.setGhost(null, 0, 0, false, 0);
        // Nothing armed: hovering a tower shows its range (selected stays the fallback).
        const hoverTile = renderer.screenToTile(e.clientX, e.clientY);
        hoverTileRef.current = hoverTile ? { x: hoverTile.tileX, y: hoverTile.tileY } : null;
        // "When you hover over such a tile it should tell you what it does."
        const buff = hoverTile
          ? world.config.buffTiles.find((t) => t.x === hoverTile.tileX && t.y === hoverTile.tileY)
          : undefined;
        const def = buff ? BUFF_TILES_BY_ID[buff.kind] : undefined;
        setTileTip(
          def
            ? {
                screenX: e.clientX,
                screenY: e.clientY,
                name: def.name,
                description: def.description,
                color: def.color,
              }
            : null,
        );
        syncFocus();
        return;
      }
      // Placement armed: the ghost's circle is the one that matters.
      setTileTip(null);
      hoverTileRef.current = null;
      renderer.setFocusRange(null);
      const item = world.economy.bench[selectedBench];
      const tile = renderer.screenToTile(e.clientX, e.clientY);
      if (!item || !tile) {
        renderer.setGhost(null, 0, 0, false, 0);
        return;
      }
      // Ask the SIM whether this is legal. Never reimplement build rules in the UI.
      const valid = canPlace(world.config.buildMask, world.towers, tile.tileX, tile.tileY);
      renderer.setGhost(
        item.defId,
        tile.tileX,
        tile.tileY,
        valid,
        rangeOnTile(getTower(item.defId).base.range, tile.tileX, tile.tileY),
      );
    },
    [selectedBench, moveMode, syncFocus, rangeOnTile],
  );

  /**
   * Placement controls: while a placement or move is ARMED, LEFT-click confirms it and
   * RIGHT-click cancels it — and a left-click on a tile the tower cannot go does
   * NOTHING, keeping the action armed instead of eating the selection. (The original
   * behaviour lost the pickup to every mis-click; a first fix had the buttons swapped.)
   */
  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      const renderer = rendererRef.current;
      const world = worldRef.current;
      if (!renderer || !world) return;
      const tile = renderer.screenToTile(e.clientX, e.clientY);
      const leftClick = e.button === 0;
      const rightClick = e.button === 2;

      // Placing from the bench takes priority — that is the action already in progress.
      if (selectedBench !== null) {
        if (leftClick) {
          if (!tile) return; // off the board — stay armed
          if (!canPlace(world.config.buildMask, world.towers, tile.tileX, tile.tileY)) return;
          dispatch({ t: 'place', benchIndex: selectedBench, tileX: tile.tileX, tileY: tile.tileY });
          setSelectedBench(null);
          renderer.setGhost(null, 0, 0, false, 0);
        } else if (rightClick) {
          // Deliberate cancel — the only right-click meaning while armed.
          setSelectedBench(null);
          renderer.setGhost(null, 0, 0, false, 0);
        }
        return;
      }

      // Relocating the selected tower: same contract. Selection follows the tower to
      // its destination, so the inspector stays open on the tower you just moved.
      if (moveMode && selectedTile !== null) {
        if (leftClick) {
          if (!tile) return;
          if (!canPlace(world.config.buildMask, world.towers, tile.tileX, tile.tileY)) return;
          const moving = world.towers.items.find(
            (t) => t.alive && t.tileX === selectedTile.x && t.tileY === selectedTile.y,
          );
          if (moving) {
            dispatch({ t: 'move', tower: moving.id, tileX: tile.tileX, tileY: tile.tileY });
            setSelectedTile({ x: tile.tileX, y: tile.tileY });
          }
          setMoveMode(false);
          renderer.setGhost(null, 0, 0, false, 0);
        } else if (rightClick) {
          setMoveMode(false);
          renderer.setGhost(null, 0, 0, false, 0);
        }
        return;
      }

      // Nothing armed: plain LEFT-click selection of whatever tower is on this tile.
      if (e.button !== 0 || !tile) return;
      const hit = world.towers.items.find(
        (t) => t.alive && t.tileX === tile.tileX && t.tileY === tile.tileY,
      );
      setSelectedTile(hit ? { x: tile.tileX, y: tile.tileY } : null);
      setMoveMode(false);
    },
    [dispatch, selectedBench, moveMode, selectedTile],
  );

  const s = snapshot;

  // Derived from the tile every frame, so it re-resolves to whatever tower now stands
  // there — including the fresh entity a merge just produced.
  const selected =
    selectedTile === null
      ? undefined
      : s?.placedTowers.find((t) => t.tileX === selectedTile.x && t.tileY === selectedTile.y);

  // Keep the board highlight on the derived tower rather than on the id captured at click
  // time, which a merge would have invalidated.
  useEffect(() => {
    rendererRef.current?.setHoveredTower(selected?.id ?? null);
  }, [selected?.id]);

  // The move ghost and focus circle read the selected tower during pointermove,
  // which outlives renders — the ref is DECLARED early (with syncFocus, next to
  // dispatch) and assigned here where `selected` exists.
  selectedTowerRef.current = selected;
  snapshotRef.current = snapshot;

  // Selection changes move the circle even while the cursor is elsewhere.
  useEffect(() => {
    syncFocus();
  }, [selected?.id, selected?.tileX, selected?.tileY, selected?.range, syncFocus]);

  // Read by the keydown handler, which outlives renders: does Esc deselect or pause?
  const hasSelectionRef = useRef(false);
  hasSelectionRef.current = selectedBench !== null || selectedTile !== null || moveMode;

  /**
   * The right-side panel reserves canvas space (per the layout: UI never covers the
   * battlefield). One slot, two tenants: a selected tower's inspector wins over the
   * shop, and closing it falls back to the shop if the build phase is still on.
   */
  const benchItem = selectedBench !== null ? (s?.bench[selectedBench] ?? null) : null;
  const sidePanel: 'inspector' | 'bench' | 'shop' | null = selected
    ? 'inspector'
    : benchItem
      ? 'bench'
      : shopOpen && s?.phase === 'build'
        ? 'shop'
        : null;
  const panelVisible = sidePanel !== null;
  useEffect(() => {
    // The stage's width just changed under the canvas; re-derive the integer scale.
    rendererRef.current?.resize();
  }, [panelVisible]);

  // Colorblind mode is a ROOT class, not per-component state: every `.chip-shape`
  // badge anywhere in the tree turns on with it (R-09-27).
  const colorblind = metaSave.settings.colorblindMode ? ' colorblind' : '';

  if (screen !== 'run') {
    return (
      <div className={`layout layout--menu${colorblind}`}>
        {screen === 'menu' && (
          <MainMenu
            save={metaSave}
            date={date}
            unlockedMaps={unlockedMaps}
            showExtras={SHOW_EXTRAS}
            onDaily={() => {
              if (metaSave.displayName === '' && metaSave.stats.runsPlayed === 0) {
                setNamePrompt(true);
                return;
              }
              setMode('daily');
              setScreen('run');
            }}
            onFreePlay={() => setScreen('maps')}
            onTree={() => setTreeOpen(true)}
            onBoard={() => setScreen('board')}
            onSettings={() => setScreen('settings')}
            onDiscovery={() => setScreen('discovery')}
            onChangeSettings={changeSettings}
          />
        )}
        {screen === 'maps' && (
          <MapSelect
            save={metaSave}
            unlockedMaps={unlockedMaps}
            onPick={(mapId) => {
              setMode('free');
              setFreeMap(mapId);
              setRunVersion((v) => v + 1);
              setScreen('run');
            }}
            onBack={() => setScreen('menu')}
          />
        )}
        {screen === 'board' && (
          <Leaderboard
            date={date}
            playerId={metaSave.playerId}
            displayName={metaSave.displayName}
            onDisplayName={changeDisplayName}
            onBack={() => setScreen('menu')}
          />
        )}
        {screen === 'settings' && (
          <SettingsScreen
            settings={metaSave.settings}
            displayName={metaSave.displayName}
            onChange={changeSettings}
            onDisplayName={changeDisplayName}
            onBack={() => setScreen('menu')}
          />
        )}
        {screen === 'discovery' && (
          <Discovery save={metaSave} onBack={() => setScreen('menu')} />
        )}
        {treeOpen && (
          <MetaTree save={metaSave} onSave={updateSave} onClose={() => setTreeOpen(false)} />
        )}
        {namePrompt && (
          <div className="pause-overlay">
            {/* Same panel as PAUSED — accent spine, skewed shell, upright content. It used
                to be `.pause` alone, which gave it the shear and none of the padding, so
                every child sat welded into a corner at an angle. */}
            <div className="pause pause--prompt">
              <span className="pause__accent" />
              <div className="pause__inner">
                <div className="pause__head">
                  <span className="pause__title">PICK A NAME</span>
                  <span className="pause__head-rule" />
                </div>
                <p className="prompt__hint">
                  Shown on the daily leaderboard. Changeable later on the leaderboard screen.
                </p>
                <input
                  className="prompt__field"
                  type="text"
                  maxLength={24}
                  placeholder="anonymous"
                  autoFocus
                  value={metaSave.displayName}
                  onChange={(e) => changeDisplayName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setNamePrompt(false);
                      setMode('daily');
                      setScreen('run');
                    }
                  }}
                />
                <button
                  type="button"
                  className="pause__action pause__action--primary prompt__go"
                  onClick={() => {
                    setNamePrompt(false);
                    setMode('daily');
                    setScreen('run');
                  }}
                >
                  <span className="pause__action-label">▶ PLAY</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  /* The in-run layout (Figma guideline, 1920×1080): no topbar — the canvas owns the top
     of the window, one bottom bar carries the whole HUD, and the shop/inspector live in
     a right-side panel that RESERVES space from the canvas rather than covering it. */
  return (
    <div className={`layout layout--game${colorblind}`}>
      <div className="main">
        <div
          className="stage"
          ref={hostRef}
          onPointerMove={onPointerMove}
          onPointerDown={onPointerDown}
          // Right-click is the CANCEL button in here — the browser menu never applies.
          onPointerLeave={() => setTileTip(null)}
          onContextMenu={(e) => e.preventDefault()}
        >
          {!ready && <p className="loading">loading…</p>}

          {/* What the ground under the cursor does. Anchored to the pointer rather than
              the tile, because the canvas is integer-scaled and a tile is 16px — a panel
              pinned to it would sit on top of the thing it is describing. */}
          {tileTip && (
            <div
              className="tile-tip"
              style={{ left: tileTip.screenX + 14, top: tileTip.screenY + 14 }}
            >
              <b style={{ color: tileTip.color }}>{tileTip.name}</b>
              <span>{tileTip.description}</span>
            </div>
          )}

          {/* The way back (playtest request). A small floating control rather than a
              panel: the whole point of dismissing the summary was to see the board
              underneath it, so the return trip must not cover the board up again. It
              lives inside the stage so it sits over the battlefield rather than over the
              bottom bar's controls. */}
          {summary && summaryHidden && !treeOpen && (
            <button
              type="button"
              className="summary-return"
              onClick={() => setSummaryHidden(false)}
            >
              ▲ View final score
            </button>
          )}
          {/* The finale's two bars. Inside the stage so it sits over the battlefield
              rather than the side panel, and gated on the snapshot rather than on the
              wave number — it appears exactly when there is a shielded boss to read. */}
          <BossBar
            boss={s?.boss ?? null}
            tick={s?.tick ?? 0}
            nameOf={(id) => getEnemy(id).name}
          />

          {/* Backlog B-1: the day's mutator must be VISIBLE — a Glass Cannon day with
              no banner reads as two bugs (inflated damage, missing Lives). */}
          {mode === 'daily' && daily?.mutator && (
            <div className="mutator-banner" title="Today's daily mutator — same for everyone">
              <b>☄ {daily.mutator.name}</b>
              <span>{daily.mutator.description}</span>
            </div>
          )}
        </div>

        {/* Synergy readout — the one element the layout left to us: a floating column in
            the battlefield's top-left, translucent so the board stays readable under it.
            Hover still lights every contributing tower (R-09-17). */}
        <div className="synergy-float">
          <SynergyPanel
            synergies={s?.synergies ?? []}
            onHoverClass={onHoverClass}
            open={metaSave.settings.synergyPanelOpen}
            onOpenChange={(v) => changeSettings({ synergyPanelOpen: v })}
          />
        </div>

        {sidePanel === 'shop' && (
          <aside className="side-panel">
            <header className="side-panel__head">
              <span className="side-panel__title">SHOP</span>
              <span className="muted">
                <kbd>1–4</kbd> buy
              </span>
              <span className="spacer" />
              <span className="gold">⬤ {s?.gold ?? 0}g</span>
              <button
                type="button"
                className={s?.shopLocked ? 'active' : ''}
                title={
                  s?.shopLocked
                    ? 'Locked — these offers survive the wave. Click to unlock.'
                    : 'Lock the shop: keep these offers through the next wave'
                }
                onClick={() => dispatch({ t: 'lockShop' })}
              >
                {s?.shopLocked ? '🔒' : '🔓'}
              </button>
              <button type="button" onClick={() => setShopOpen(false)} title="Close the shop">
                ✕
              </button>
            </header>
            <div className="side-panel__cards">
              {(s?.shop ?? []).map((slot, i) => (
                <ShopCard
                  key={i}
                  slot={slot}
                  index={i}
                  gold={s?.gold ?? 0}
                  benchFull={(s?.bench ?? []).every((b) => b !== null)}
                  buildPhase={s?.phase === 'build'}
                  onBuy={(idx) => dispatch({ t: 'buy', slot: idx })}
                />
              ))}
            </div>
            <button
              type="button"
              disabled={(s?.gold ?? 0) < (s?.rerollCost ?? 0)}
              title={
                (s?.gold ?? 0) < (s?.rerollCost ?? 0)
                  ? `Reroll costs ${s?.rerollCost ?? 0}g — you have ${s?.gold ?? 0}g`
                  : 'Reroll the shop (R)'
              }
              onClick={() => dispatch({ t: 'reroll' })}
            >
              ⟳ Reroll <span className="gold">{s?.rerollCost ?? 0}g</span> <kbd>R</kbd>
            </button>

            {/* Backlog B-4 — what's coming, so the build phase is an informed decision. */}
            {s?.nextWave && s.nextWave.length > 0 && (
              <div className="next-wave">
                <span className="label">
                  Next wave{s.nextWaveIsBoss && <b className="warn"> ⚠ boss</b>}
                </span>
                <div className="next-wave__chips">
                  {s.nextWave.map((entry) => {
                    // Hovering a chip explains the archetype (playtest request). The text
                    // is derived from the def, so it cannot disagree with what spawns.
                    const brief = enemyBrief(getEnemy(entry.defId), (id) => getEnemy(id).name);
                    return (
                      <span
                        key={entry.defId}
                        className={`next-wave__chip${entry.boss ? ' next-wave__chip--boss' : ''}`}
                        tabIndex={0}
                      >
                        {entry.count}× {brief.name}
                        <span className="enemy-tip" role="tooltip">
                          <b className="enemy-tip__name">{brief.name}</b>
                          <span className="enemy-tip__stats">{brief.stats}</span>
                          {brief.traits.map((trait) => (
                            <span key={trait} className="enemy-tip__trait">
                              {trait}
                            </span>
                          ))}
                        </span>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </aside>
        )}

        {sidePanel === 'bench' && benchItem && selectedBench !== null && (
          <aside className="side-panel">
            <header className="side-panel__head">
              <span className="side-panel__title">BENCH</span>
              <span className="spacer" />
              <button
                type="button"
                onClick={() => setSelectedBench(null)}
                title="Deselect (right-click / Esc)"
              >
                ✕
              </button>
            </header>
            <BenchInspector
              item={benchItem}
              onSell={() => {
                dispatch({ t: 'sellBench', benchIndex: selectedBench });
                setSelectedBench(null);
              }}
            />
          </aside>
        )}

        {sidePanel === 'inspector' && selected && (
          <aside className="side-panel">
            <header className="side-panel__head">
              <span className="side-panel__title">TOWER</span>
              <span className="spacer" />
              <button type="button" onClick={() => setSelectedTile(null)} title="Deselect (Esc)">
                ✕
              </button>
            </header>
            <TowerInspector
              tower={selected}
              surgeKey={
                // Its position among the support towers on the board, which is exactly
                // what the number keys index — one source of truth for both.
                selected.support
                  ? ((s?.placedTowers ?? []).filter((t) => t.support).findIndex(
                      (t) => t.id === selected.id,
                    ) + 1 || null)
                  : null
              }
              standingOn={(() => {
                const tile = worldRef.current?.config.buffTiles.find(
                  (t) => t.x === selected.tileX && t.y === selected.tileY,
                );
                const def = tile ? BUFF_TILES_BY_ID[tile.kind] : undefined;
                return def ? { name: def.name, description: def.description, color: def.color } : null;
              })()}
              breakdown={worldRef.current?.statBreakdownFor(selected.id) ?? []}
              phase={s?.phase ?? 'build'}
              placementsLeft={s?.placementsLeft ?? 0}
              moveMode={moveMode}
              onMoveMode={setMoveMode}
              onDeselect={() => setSelectedTile(null)}
              dispatch={dispatch}
            />
          </aside>
        )}
      </div>

      <footer className="bottombar bottombar--game">
        {/* The one thing the player operates rather than places, kept where the eye can
            find it mid-wave instead of behind a click on a small shape on the board. */}
        <SurgeBar
          towers={s?.placedTowers ?? []}
          glyphOf={(id) => getTower(id).glyph}
          colorOf={(id) => CLASS_COLORS[getTower(id).classes[0] ?? 'warden'].color}
          nameOf={(id) => getTower(id).name}
          dispatch={dispatch}
        />
        {/* Stats box, bottom-left per the layout: the run's three numbers plus the
            tower cap. fps hides in the tooltip; ` still opens the debug overlay. */}
        <div className="hud-stats" title={`${Math.round(fps)} fps — \` toggles debug`}>
          <span>
            Wave{' '}
            <b>
              {s?.wave ?? 0}/{s?.totalWaves ?? 0}
            </b>
            {s?.currentWaveIsBoss && <b className="warn"> ⚠ BOSS</b>}
            {s?.phase === 'build' && s.nextWaveIsBoss && (
              <span className="warn"> · boss next</span>
            )}
          </span>
          <span>
            Lives{' '}
            <b className={(s?.coreHp ?? 0) <= Math.ceil((s?.coreMaxHp ?? 0) / 4) ? 'warn' : ''}>
              ♥ {s?.coreHp ?? 0}/{s?.coreMaxHp ?? 0}
            </b>
          </span>
          {/* No Money line — gold lives in the shop panel header, where it is spent. */}
          <span className={(s?.towersPlaced ?? 0) >= (s?.maxTowers ?? 0) ? 'warn' : 'muted'}>
            towers {s?.towersPlaced ?? 0}/{s?.maxTowers ?? 0}
          </span>
        </div>

        <span className="spacer" />

        <div className="group">
          {s?.phase === 'build' && !panelVisible && (
            <button type="button" onClick={() => setShopOpen(true)} title="Reopen the shop">
              Shop
            </button>
          )}
          <span className="label">
            Bench <kbd>Esc</kbd>
          </span>
          {(s?.bench ?? []).map((item, i) => (
            <button
              key={i}
              type="button"
              className={`bench-slot${selectedBench === i ? ' active' : ''}${
                item &&
                item.star === 1 &&
                (s?.shop ?? []).some((slot) => slot?.mergesOnBench && slot.defId === item.defId)
                  ? ' bench-slot--merge'
                  : ''
              }`}
              disabled={!item}
              title={item ? 'Select, then click a tile to place — right-click cancels' : undefined}
              onClick={() => setSelectedBench(selectedBench === i ? null : i)}
            >
              {item ? `${getTower(item.defId).glyph}${item.star > 1 ? `★${item.star}` : ''}` : '·'}
            </button>
          ))}
        </div>

        <div className="group">
          <span className="label">Placements</span>
          <span className="place-pips" title="Placements left this build phase">
            {Array.from({ length: Math.max(s?.placementsLeft ?? 0, 2) }, (_, i) => (
              <i
                key={i}
                className={
                  i < (s?.placementsLeft ?? 0) ? 'place-pip place-pip--free' : 'place-pip place-pip--used'
                }
              />
            ))}
            <b>{s?.placementsLeft ?? 0}</b>
          </span>
        </div>

        <span className="spacer" />

        {/* Daily ↔ free play. Free play is where the unlock tree applies (R-08-19a);
            the daily deliberately ignores it (R-08-22). Hidden with the tree. */}
        {SHOW_EXTRAS && (
        <div className="group">
          <span className="label">Mode</span>
          <button
            type="button"
            className={mode === 'daily' ? 'active' : ''}
            onClick={() => setMode('daily')}
            title="The shared seeded daily — everyone plays the same run, tree ignored"
          >
            daily
          </button>
          <button
            type="button"
            className={mode === 'free' ? 'active' : ''}
            onClick={() => setMode('free')}
            title="Your unlocks, your effect nodes, your choice of map"
          >
            free play
          </button>
          {mode === 'free' && (
            <>
              {unlockedMaps.map((mapId) => (
                <button
                  key={mapId}
                  type="button"
                  className={freeMap === mapId ? 'active' : ''}
                  onClick={() => setFreeMap(mapId)}
                >
                  {getMap(mapId).name}
                </button>
              ))}
              <button
                type="button"
                title="Restart with a fresh seed and your current unlocks"
                onClick={() => setRunVersion((v) => v + 1)}
              >
                ↻ new run
              </button>
            </>
          )}
        </div>
        )}

        {/* Day rotator (dev tool — hidden in the public build, which plays only today).
            Every daily is a fresh map, wave script and mutator derived from the date,
            and the only way to know the generator produces days worth playing is to
            walk through them and play some. The readout is the generator's own report:
            which mutator it settled on, how far the reference player got, and how many
            candidate maps it burned getting there. */}
        {SHOW_EXTRAS && mode === 'daily' && (
          <div className="group">
            <span className="label">Daily</span>
            <button type="button" onClick={() => setDayOffset((d) => d - 1)} title="Previous day">
              ◀
            </button>
            <span>{date}</span>
            <button type="button" onClick={() => setDayOffset((d) => d + 1)} title="Next day">
              ▶
            </button>
            <button
              type="button"
              disabled={dayOffset === 0}
              onClick={() => setDayOffset(0)}
              title="Back to today"
            >
              today
            </button>
            {daily && (
              <span className="muted">
                {daily.generated ? (
                  <>
                    seed {daily.seed.toString().slice(0, 8)}… · {daily.mutator?.name ?? 'no mutator'} ·
                    probe w{daily.probeWave} · {daily.attempts} tries · {daily.genMs}ms
                  </>
                ) : (
                  <span className="warn">
                    fell back to an authored map — generation exhausted its attempts
                  </span>
                )}
              </span>
            )}
          </div>
        )}

        {/* Bottom-right cluster per the layout: Autostart, Next wave, speeds under it. */}
        <div className="group">
          <button
            type="button"
            className={`autostart${autoStart ? ' active' : ''}`}
            title={
              autoStart
                ? 'Waves start on their own after a short countdown — click to take back control'
                : 'Start the next wave automatically when this one clears'
            }
            onClick={() => setAutoStart(!autoStart)}
          >
            Autostart {autoStart ? 'on' : 'off'}
          </button>
          <div className="wave-cluster">
            <button
              type="button"
              className="primary"
              disabled={s?.phase !== 'build'}
              title={s?.phase !== 'build' ? 'A wave is already running' : 'Start the wave (Space)'}
              onClick={() => dispatch({ t: 'startWave' })}
            >
              {autoCountdown !== null
                ? `▶ Auto in ${Math.ceil(autoCountdown / 1000)}…`
                : '▶ Next wave '}
              {autoCountdown === null && <kbd>Space</kbd>}
            </button>
            {/* Time moving on its own needs to say so, or it reads as a stutter. */}
            {skipping && (
              <span
                className="skip-badge"
                title="Nothing has taken damage for a moment — skipping ahead"
              >
                ⏩ {IDLE_SKIP_SPEED}×
              </span>
            )}
            <div className="speeds">
              {SELECTABLE_SPEEDS.map((v) => (
                <button
                  key={v}
                  type="button"
                  className={speed === v ? 'active' : ''}
                  onClick={() => {
                    setSpeed(v);
                    speedRef.current = v;
                    if (loopRef.current) loopRef.current.speed = v;
                  }}
                >
                  {v}&times;
                </button>
              ))}
            </div>
          </div>
          <button type="button" title="Pause (Esc)" onClick={() => setPaused(true)}>
            ❚❚
          </button>
        </div>
      </footer>

      {/* Pause menu (Esc): the loop is stopped dead, so the frame behind is frozen.
          Menu / Restart per the layout; the comfort toggles live here now that the
          bottom bar no longer has room — they persist via the same changeSettings
          path as the settings screen (R-04-11/R-04-21: shake 0 and numbers off are
          fully supported states). */}
      {paused && !summary && (
        <div className="pause-overlay" onClick={() => setPaused(false)}>
          <div className="pause" onClick={(e) => e.stopPropagation()}>
            <span className="pause__accent" />
            <div className="pause__inner">
              <div className="pause__head">
                <span className="pause__title">PAUSED</span>
                <span className="pause__head-rule" />
                {/* Which run you are actually in. A generated daily has no authored name,
                    so it says its date — the thing that identifies it to another player. */}
                <span className="pause__map">
                  {mode === 'free' ? getMap(freeMap).name : date}
                </span>
              </div>

              <div className="pause__actions">
                <button
                  type="button"
                  className="pause__action pause__action--primary"
                  onClick={() => setPaused(false)}
                >
                  <span className="pause__action-label">▶ RESUME</span>
                  <span className="pause__action-key">ESC</span>
                </button>
                <button
                  type="button"
                  className="pause__action"
                  title={
                    modeRef.current === 'daily'
                      ? 'Restart this daily from wave 0 — same seed, same run'
                      : 'Restart with a fresh seed'
                  }
                  onClick={() => {
                    setPaused(false);
                    setRunVersion((v) => v + 1);
                  }}
                >
                  <span className="pause__action-label">↻ RESTART</span>
                  <span className="pause__action-note">
                    {modeRef.current === 'daily' ? 'same seed' : 'fresh seed'}
                  </span>
                </button>
                <button
                  type="button"
                  className="pause__action pause__action--leave"
                  title="Back to the main menu — abandons the current run"
                  onClick={() => {
                    setPaused(false);
                    setScreen('menu');
                  }}
                >
                  <span className="pause__action-label">MENU</span>
                  <span className="pause__action-note">abandons the run</span>
                </button>
              </div>

              <div className="pause__section">FEEL</div>
              <div className="pause__feel">
                <button
                  type="button"
                  className={vfxSettings.screenshakeScale > 0 ? 'active' : ''}
                  onClick={() =>
                    changeSettings({ screenshakeScale: vfxSettings.screenshakeScale > 0 ? 0 : 1 })
                  }
                >
                  SHAKE {vfxSettings.screenshakeScale > 0 ? 'ON' : 'OFF'}
                </button>
                <button
                  type="button"
                  className={vfxSettings.damageNumbers !== 'off' ? 'active' : ''}
                  onClick={() =>
                    changeSettings({
                      damageNumbers:
                        vfxSettings.damageNumbers === 'all'
                          ? 'crits-only'
                          : vfxSettings.damageNumbers === 'crits-only'
                            ? 'off'
                            : 'all',
                    })
                  }
                >
                  DMG {vfxSettings.damageNumbers.toUpperCase()}
                </button>
                <button
                  type="button"
                  className={vfxSettings.hitstopEnabled ? 'active' : ''}
                  onClick={() => changeSettings({ hitstopEnabled: !vfxSettings.hitstopEnabled })}
                >
                  HITSTOP {vfxSettings.hitstopEnabled ? 'ON' : 'OFF'}
                </button>
              </div>

              {/* Volume sliders live HERE because this is the only settings surface the
                  public build can reach mid-run (SettingsScreen sits behind SHOW_EXTRAS).
                  Music is audible while dragged — pause stops the frame loop, not the
                  AudioContext — so the slider tunes against the actual track. */}
              <div className="pause__section">AUDIO</div>
              <div className="pause__audio">
                {(
                  [
                    ['Master', metaSave.settings.masterVolume, (v: number) => ({ masterVolume: v })],
                    ['Effects', metaSave.settings.sfxVolume, (v: number) => ({ sfxVolume: v })],
                    ['Music', metaSave.settings.musicVolume, (v: number) => ({ musicVolume: v })],
                  ] as const
                ).map(([label, value, patch]) => (
                  <label key={label}>
                    <span>{label.toUpperCase()}</span>
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.05}
                      value={value}
                      onChange={(e) => changeSettings(patch(Number(e.target.value)))}
                    />
                    <span>{Math.round(value * 100)}%</span>
                  </label>
                ))}
              </div>

              {SHOW_EXTRAS && (
                <button type="button" className="pause__extra" onClick={() => setTreeOpen(true)}>
                  UNLOCK TREE · ✦ {shardsAvailable(metaSave)}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {treeOpen && (
        <MetaTree save={metaSave} onSave={updateSave} onClose={() => setTreeOpen(false)} />
      )}

      {summary && !summaryHidden && !treeOpen && (
        <RunSummary
          data={summary}
          submission={submitState}
          // Shard lines mean nothing while the tree is hidden — the award still banks.
          award={SHOW_EXTRAS ? lastAward : null}
          feedback={
            // Once a day, not once a run — see `lastFeedbackDate`. Both answering and
            // dismissing count as asked, so "not now" really means not today.
            !feedbackDue ? null : (
              <FeedbackForm
                onDismiss={() => updateSave({ ...metaSave, lastFeedbackDate: date })}
                onSubmit={(answers) => {
                  updateSave({ ...metaSave, lastFeedbackDate: date });
                  // Deliberately not awaited: the player has already been thanked, and a
                  // failed send is one lost opinion rather than a lost score.
                  void sendFeedback(answers, {
                    playerId: metaSave.playerId,
                    date: summary.mode === 'daily' ? date : null,
                    victory: summary.victory,
                    wavesCleared: summary.wavesCleared,
                    score: summary.score,
                    coreHp: summary.coreHp,
                    simVersion: SIM_VERSION,
                    clientVersion: GAME_VERSION,
                    mutator: summary.mutator,
                    towers: summary.towers.map((t) => ({
                      defId: t.defId,
                      star: t.star,
                      damageDealt: t.damageDealt,
                    })),
                  });
                }}
              />
            )
          }
          onPlayAgain={() => {
            // Daily rebuild = the same date-derived seed, a true "retry same seed";
            // free-play rebuild = a fresh wall-clock seed, a true "new run" (R-09-25).
            setSummary(null);
            setSummaryHidden(false);
            setRunVersion((v) => v + 1);
          }}
          onMenu={() => {
            setSummary(null);
            setSummaryHidden(false);
            setScreen('menu');
          }}
          onClose={() => setSummaryHidden(true)}
        />
      )}

    </div>
  );
}
